import httpClient from './httpClient';
import { parseApiError } from '../utils/errorHandler';

const REPORTS_BASE_PATH = '/api/relatorios';

export const REPORT_FORMAT = {
    PDF: 'PDF',
    XLSX: 'XLSX',
};

export const REPORT_EXECUTION_MODE = {
    SYNC: 'SYNC',
    ASYNC: 'ASYNC',
};

export const REPORT_JOB_STATUS = {
    QUEUED: 'QUEUED',
    PROCESSING: 'PROCESSING',
    READY: 'READY',
    FAILED: 'FAILED',
};

const FORMAT_EXTENSION = {
    [REPORT_FORMAT.PDF]: 'pdf',
    [REPORT_FORMAT.XLSX]: 'xlsx',
};

const FORMAT_CONTENT_TYPE = {
    [REPORT_FORMAT.PDF]: 'application/pdf',
    [REPORT_FORMAT.XLSX]:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_POLL_TIMEOUT_MS = 120000;

/**
 * Erro de domínio dos relatórios (mantém `details`/`status` para a UI).
 * @param {string} message
 * @param {{ details?: unknown[], status?: number|null }} [extra]
 * @returns {Error}
 */
function createReportError(message, { details = [], status = null } = {}) {
    const error = new Error(message);
    error.details = details;
    error.status = status;
    return error;
}

/**
 * Extrai o nome do arquivo do header Content-Disposition, se presente.
 * @param {Response} response
 * @returns {string|null}
 */
function extractFilename(response) {
    const disposition =
        response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    if (!disposition) return null;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim());
        } catch {
            return utf8Match[1].trim();
        }
    }

    const match = disposition.match(/filename="?([^";]+)"?/i);
    return match?.[1]?.trim() || null;
}

/**
 * @param {string} type
 * @param {string} format
 * @returns {string}
 */
function defaultFilename(type, format) {
    const ext = FORMAT_EXTENSION[format] || String(format).toLowerCase();
    return `relatorio-${String(type).toLowerCase()}.${ext}`;
}

/**
 * @param {Blob} blob
 * @param {string} filename
 * @param {string|null} [contentType]
 */
function toReportFileResult(blob, filename, contentType = null) {
    return {
        blob,
        filename,
        contentType: contentType || blob.type || null,
    };
}

/**
 * Lista os tipos de relatório disponíveis para o usuário autenticado.
 * GET /api/relatorios/tipos
 * @returns {Promise<Array<{
 *   type: string,
 *   executionMode: string,
 *   supportedFormats: string[],
 *   allowedRoles: string[],
 * }>>}
 */
export async function listReportTypes() {
    const response = await httpClient.get(`${REPORTS_BASE_PATH}/tipos`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
}

/**
 * Consulta o status de um job de relatório.
 * GET /api/relatorios/jobs/{jobId}
 * @param {string|number} jobId
 * @returns {Promise<{
 *   jobId: number,
 *   status: string,
 *   type: string,
 *   format: string,
 *   downloadUrl?: string|null,
 *   downloadUrlExpiresAt?: string|null,
 *   errorMessage?: string|null,
 *   promotedFromSync?: boolean,
 * }>}
 */
export async function getReportJob(jobId) {
    const response = await httpClient.get(
        `${REPORTS_BASE_PATH}/jobs/${encodeURIComponent(String(jobId))}`
    );
    if (!response.ok) {
        throw await parseApiError(response);
    }
    return response.json();
}

/**
 * Aguarda o job chegar em READY (ou falhar).
 * @param {string|number} jobId
 * @param {{
 *   intervalMs?: number,
 *   timeoutMs?: number,
 *   signal?: AbortSignal,
 *   onStatus?: (job: object) => void,
 * }} [options]
 */
export async function waitForReportJob(jobId, options = {}) {
    const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const timeoutMs = options.timeoutMs ?? DEFAULT_POLL_TIMEOUT_MS;
    const startedAt = Date.now();

    while (true) {
        if (options.signal?.aborted) {
            const abortError = new Error('Emissão de relatório cancelada.');
            abortError.name = 'AbortError';
            throw abortError;
        }

        const job = await getReportJob(jobId);
        options.onStatus?.(job);

        if (job.status === REPORT_JOB_STATUS.READY) {
            return job;
        }

        if (job.status === REPORT_JOB_STATUS.FAILED) {
            throw createReportError(job.errorMessage || 'Falha ao gerar o relatório.');
        }

        if (Date.now() - startedAt >= timeoutMs) {
            throw createReportError('Tempo esgotado ao aguardar a geração do relatório.');
        }

        await new Promise((resolve, reject) => {
            const timer = setTimeout(resolve, intervalMs);
            if (options.signal) {
                const onAbort = () => {
                    clearTimeout(timer);
                    const abortError = new Error('Emissão de relatório cancelada.');
                    abortError.name = 'AbortError';
                    reject(abortError);
                };
                if (options.signal.aborted) {
                    onAbort();
                    return;
                }
                options.signal.addEventListener('abort', onAbort, { once: true });
            }
        });
    }
}

/**
 * Baixa o arquivo a partir de uma URL pré-assinada (sem auth da API).
 * @param {string} downloadUrl
 * @param {{ type?: string, format?: string, filename?: string }} [meta]
 */
export async function downloadReportFromUrl(downloadUrl, meta = {}) {
    const response = await fetch(downloadUrl);
    if (!response.ok) {
        throw createReportError('Não foi possível baixar o relatório gerado.', {
            status: response.status,
        });
    }

    const blob = await response.blob();
    const contentType =
        response.headers.get('content-type') ||
        FORMAT_CONTENT_TYPE[meta.format] ||
        blob.type ||
        null;
    const filename =
        extractFilename(response) ||
        meta.filename ||
        defaultFilename(meta.type || 'relatorio', meta.format || REPORT_FORMAT.PDF);

    return toReportFileResult(blob, filename, contentType);
}

/**
 * Emite um relatório e resolve para o arquivo pronto (SYNC ou ASYNC).
 *
 * - `200 OK`: stream binário imediato
 * - `202 Accepted`: polling em `/jobs/{jobId}` até `READY`, depois baixa via `downloadUrl`
 *
 * @param {{ type: string, format: string, parameters?: Record<string, unknown> }} payload
 * @param {{
 *   intervalMs?: number,
 *   timeoutMs?: number,
 *   signal?: AbortSignal,
 *   onStatus?: (job: object) => void,
 * }} [options]
 * @returns {Promise<{
 *   blob: Blob,
 *   filename: string,
 *   contentType: string|null,
 *   mode: 'SYNC'|'ASYNC',
 *   promotedFromSync: boolean,
 *   jobId?: number|null,
 * }>}
 */
export async function emitReport(payload, options = {}) {
    const { type, format, parameters = {} } = payload || {};
    if (!type || !format) {
        throw new Error('emitReport requer type e format.');
    }

    const response = await httpClient.post(REPORTS_BASE_PATH, {
        type,
        format,
        parameters,
    });

    const promotedFromSyncHeader =
        (response.headers.get('X-Report-Delivery') || '').toUpperCase() === 'ASYNC_PROMOTED';

    if (response.status === 202) {
        const accepted = await response.json();
        const jobId = accepted?.jobId;
        if (jobId == null) {
            throw createReportError('Resposta assíncrona sem jobId.', { status: 202 });
        }

        const readyJob = await waitForReportJob(jobId, options);
        if (!readyJob.downloadUrl) {
            throw createReportError('Relatório pronto, mas sem URL de download.');
        }

        const file = await downloadReportFromUrl(readyJob.downloadUrl, {
            type: readyJob.type || type,
            format: readyJob.format || format,
        });

        return {
            ...file,
            mode: REPORT_EXECUTION_MODE.ASYNC,
            promotedFromSync: Boolean(accepted?.promotedFromSync) || promotedFromSyncHeader,
            jobId,
        };
    }

    if (!response.ok) {
        throw await parseApiError(response);
    }

    const blob = await response.blob();
    const contentType =
        response.headers.get('content-type') || FORMAT_CONTENT_TYPE[format] || blob.type || null;
    const filename = extractFilename(response) || defaultFilename(type, format);

    return {
        ...toReportFileResult(blob, filename, contentType),
        mode: REPORT_EXECUTION_MODE.SYNC,
        promotedFromSync: false,
        jobId: null,
    };
}

/**
 * Cliente genérico para emissão de relatórios.
 * `type` é fixo na criação. `format` pode ser fixo na criação ou informado em `options.format` no emit.
 *
 * @param {{ type: string, format?: string }} config
 */
export function createReportClient({ type, format } = {}) {
    if (!type) {
        throw new Error('createReportClient requer type.');
    }

    return {
        type,
        format: format || null,
        /**
         * @param {Record<string, unknown>} [parameters]
         * @param {{
         *   format?: string,
         *   intervalMs?: number,
         *   timeoutMs?: number,
         *   signal?: AbortSignal,
         *   onStatus?: (job: object) => void,
         * }} [options]
         */
        emit(parameters = {}, options = {}) {
            const { format: formatOverride, ...pollOptions } = options;
            const resolvedFormat = formatOverride || format;
            if (!resolvedFormat) {
                throw new Error('emit requer format (no client ou em options.format).');
            }
            return emitReport(
                { type, format: resolvedFormat, parameters },
                pollOptions
            );
        },
    };
}

/**
 * Abre o blob em nova aba (útil para PDF / impressão).
 * @param {Blob} blob
 */
export function openReportBlob(blob) {
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, '_blank');
    if (!opened) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Força o download do arquivo (útil para XLSX ou quando popup é bloqueado).
 * @param {Blob} blob
 * @param {string} filename
 */
export function downloadReportBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'relatorio';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Entrega o arquivo ao usuário: PDF abre em nova aba; demais formatos fazem download.
 * @param {{ blob: Blob, filename: string, contentType?: string|null }} file
 */
export function deliverReportFile(file) {
    const contentType = String(file?.contentType || file?.blob?.type || '').toLowerCase();
    const filename = file?.filename || 'relatorio';
    const isPdf =
        contentType.includes('pdf') || String(filename).toLowerCase().endsWith('.pdf');

    if (isPdf) {
        openReportBlob(file.blob);
        return;
    }

    downloadReportBlob(file.blob, filename);
}

/**
 * Celular ou tablet. Computador permanece no fluxo de abrir/baixar o arquivo.
 * @returns {boolean}
 */
export function isHandheldDevice() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
    if (navigator.userAgentData?.mobile === true) return true;
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;

    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches === true;
    const noHover = window.matchMedia?.('(hover: none)')?.matches === true;
    return coarse && noHover;
}

function safePdfFilename(filename) {
    const base = String(filename || 'protocolo-entrega')
        .replace(/\.pdf$/i, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return `${base || 'protocolo-entrega'}.pdf`;
}

/**
 * Garante nome e tipo que o compartilhamento de arquivo do celular aceita.
 * @param {string} filename
 * @param {string} type
 * @returns {{ filename: string, type: string }}
 */
function normalizeShareablePdf(filename, type) {
    const rawName = String(filename || 'protocolo-entrega').trim() || 'protocolo-entrega';
    const rawType = String(type || '');
    const looksPdf = /pdf/i.test(rawType) || /\.pdf$/i.test(rawName);
    if (!looksPdf) {
        return { filename: rawName, type: rawType || 'application/octet-stream' };
    }

    return {
        filename: /\.pdf$/i.test(rawName) ? rawName : `${rawName}.pdf`,
        type: 'application/pdf',
    };
}

/**
 * @param {{ blob: Blob, filename?: string, contentType?: string|null }} file
 * @returns {File}
 */
export function toShareableReportFile(file) {
    const blob = file?.blob;
    if (!blob) {
        throw new Error('Arquivo do relatório não encontrado.');
    }

    const normalized = normalizeShareablePdf(
        file.filename || 'protocolo-entrega.pdf',
        file.contentType || blob.type || ''
    );
    if (
        typeof File !== 'undefined'
        && blob instanceof File
        && blob.name === normalized.filename
        && blob.type === normalized.type
    ) {
        return blob;
    }
    return new File([blob], normalized.filename, { type: normalized.type });
}

/**
 * Folha nativa só em celular, com HTTPS e `navigator.share`.
 * Sem isso o PDF abre em `blob:`, como no computador.
 * @param {{ blob?: Blob } | null | undefined} file
 * @returns {boolean}
 */
export function canNativeShareReportFile(file) {
    if (!file?.blob || !isHandheldDevice()) return false;
    if (typeof window === 'undefined' || window.isSecureContext !== true) return false;
    return typeof navigator.share === 'function';
}

/**
 * Copia o PDF para um File antes do toque. Criar o arquivo dentro do clique
 * faz o celular recusar a folha de compartilhamento.
 * @param {{ blob: Blob, filename?: string, contentType?: string|null }} file
 * @returns {Promise<File>}
 */
function readBlobBytes(blob) {
    if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Não foi possível ler o PDF.'));
        reader.readAsArrayBuffer(blob);
    });
}

export async function prepareShareableReportFile(file) {
    const blob = file?.blob;
    if (!blob) {
        throw new Error('Arquivo do relatório não encontrado.');
    }

    const buffer = await readBlobBytes(blob);
    const headerBytes = new Uint8Array(buffer.slice(0, 5));
    const header = String.fromCharCode(...headerBytes);
    const declaredPdf = /pdf/i.test(`${file.contentType || ''}${blob.type || ''}`)
        || /\.pdf$/i.test(file.filename || '');
    const isPdf = header.startsWith('%PDF') || declaredPdf;
    const normalized = isPdf
        ? { filename: safePdfFilename(file.filename), type: 'application/pdf' }
        : normalizeShareablePdf(file.filename, file.contentType || blob.type || '');

    return new File([buffer], normalized.filename, { type: normalized.type });
}

/**
 * Abre a folha de compartilhamento. O `file` precisa já estar preparado,
 * e esta função precisa ser a primeira chamada do toque, sem await antes.
 * @param {File} file
 * @returns {Promise<void>}
 */
export function sharePreparedFile(file) {
    try {
        return navigator.share({ files: [file] });
    } catch (err) {
        return Promise.reject(err);
    }
}

/**
 * @param {{ blob: Blob, filename?: string, contentType?: string|null }} file
 * @returns {Promise<void>}
 */
export function shareReportFile(file) {
    const shareFile = toShareableReportFile(file);
    return sharePreparedFile(shareFile);
}

/**
 * No celular, reserva o PDF para o botão Compartilhar e não navega para `blob:`.
 * No computador, abre ou baixa o arquivo como antes.
 * @param {{ blob: Blob, filename?: string, contentType?: string|null }} file
 * @returns {Promise<'needs-gesture'|'delivered'>}
 */
export function presentReportFile(file) {
    if (canNativeShareReportFile(file)) {
        return Promise.resolve('needs-gesture');
    }

    deliverReportFile(file);
    return Promise.resolve('delivered');
}

/** Protocolo de entrega em PDF. */
export const deliveryProtocolReportClient = createReportClient({
    type: 'DELIVERY_PROTOCOL',
    format: REPORT_FORMAT.PDF,
});

const reportService = {
    REPORT_FORMAT,
    REPORT_EXECUTION_MODE,
    REPORT_JOB_STATUS,
    listReportTypes,
    getReportJob,
    waitForReportJob,
    downloadReportFromUrl,
    emitReport,
    createReportClient,
    openReportBlob,
    downloadReportBlob,
    deliverReportFile,
    isHandheldDevice,
    toShareableReportFile,
    canNativeShareReportFile,
    prepareShareableReportFile,
    sharePreparedFile,
    shareReportFile,
    presentReportFile,
    deliveryProtocolReportClient,
};

export default reportService;
